import CopyAllIcon from "@mui/icons-material/CopyAllRounded";
import { Alert, AlertTitle, IconButton, Link, Stack, Typography } from "@mui/material";
import type { ExecutionState } from "../../hooks/useExecutionState";

interface Props {
  executionState: ExecutionState;
  setExecutionState: (state: ExecutionState) => void;
}

const ExecutionStatusAlert = ({ executionState, setExecutionState }: Props) => {
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (executionState.status === "success" || executionState.status === "warning") {
    const severity = executionState.status === "success" ? "success" : "warning";
    const defaultTitle = executionState.status === "success" ? "Success" : "Warning";
    const copyText = executionState.copyText ?? executionState.details?.join("\n");
    return (
      <Alert
        severity={severity}
        sx={{ maxWidth: "32rem", position: "relative" }}
        onClose={() => setExecutionState({ status: "idle" })}
      >
        <AlertTitle>{executionState.text || defaultTitle}</AlertTitle>
        {copyText && (
          <IconButton
            sx={{ position: "absolute", top: 32, right: 8 }}
            color="inherit"
            onClick={() => handleCopyToClipboard(copyText)}
          >
            <CopyAllIcon />
          </IconButton>
        )}
        {executionState.details && executionState.details.length > 0 && (
          <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
            {executionState.details.map((detail, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <Typography key={index}>{detail}</Typography>
            ))}
          </Stack>
        )}
        {executionState.links && executionState.links.length > 0 && (
          <Stack spacing={0.5} sx={{ alignItems: "flex-start", mt: 1 }}>
            {executionState.links.map((link, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <Link key={index} href={link.href} target="_blank" rel="noreferrer">
                {link.label}
              </Link>
            ))}
          </Stack>
        )}
      </Alert>
    );
  }

  if (executionState.status === "error") {
    return (
      <Alert severity="error" sx={{ maxWidth: "32rem" }} onClose={() => setExecutionState({ status: "idle" })}>
        <AlertTitle>
          {executionState.error ? `API Error - status ${executionState.error.code}` : executionState.text || "Error"}
        </AlertTitle>
        {executionState.errorMessage}
        {executionState.error && executionState.error.message}
      </Alert>
    );
  }

  return null;
};

export default ExecutionStatusAlert;
